/**
 * Format a quantity / unit value for display: thousands-separated, up to 3
 * decimals, but WITHOUT forced trailing zeros — so 5 → "5", 2.5 → "2.5",
 * 1.25 → "1.25".
 *
 * Quantities can be fractional UOM (e.g. 1.5 L, 0.25 kg), so unlike currency we
 * never round to whole numbers — that would misreport stock.
 */
export const fmtQty = (v: any) =>
  Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

/**
 * Human-friendly UOM display for a MEASURE (a pack size / volume / weight).
 * Sub-unit-aware so a fractional quantity reads naturally instead of as a decimal:
 *
 *   0.25 LTR → "250 ml"   0.5 KG → "500 g"   1 LTR → "1 L"   25 KG → "25 kg"
 *
 * Millilitre / gram inputs roll the other way once they reach a whole base unit
 * (1500 g → "1.5 kg", 2000 ml → "2 L"). Count / unknown UOMs (NOS, BAG, BOX and
 * any custom master code) are left as-is: "<qty> <UOM>".
 *
 * NOTE: only use this for an actual measured quantity (pack_size). Stock / order
 * quantities are a COUNT OF PACKS, not a volume — keep those on fmtQty/fmtPackQty.
 */
export function fmtUom(value: any, uom?: string): string {
  const v = Number(value);
  const code = (uom ?? '').trim();
  if (!Number.isFinite(v)) return code ? `${value} ${code}` : String(value ?? '').trim();
  const U = code.toUpperCase();
  const abs = Math.abs(v);

  // Volume — litres ⇄ millilitres
  if (U === 'LTR' || U === 'L' || U === 'LITRE' || U === 'LITER') {
    return v !== 0 && abs < 1 ? `${fmtQty(v * 1000)} ml` : `${fmtQty(v)} L`;
  }
  if (U === 'ML') {
    return abs >= 1000 ? `${fmtQty(v / 1000)} L` : `${fmtQty(v)} ml`;
  }
  // Weight — kilograms ⇄ grams
  if (U === 'KG' || U === 'KGS' || U === 'KILOGRAM' || U === 'KILOGRAMME') {
    return v !== 0 && abs < 1 ? `${fmtQty(v * 1000)} g` : `${fmtQty(v)} kg`;
  }
  if (U === 'G' || U === 'GM' || U === 'GMS' || U === 'GRAM' || U === 'GRAMME') {
    return abs >= 1000 ? `${fmtQty(v / 1000)} kg` : `${fmtQty(v)} g`;
  }
  // Counts / unknown UOM — show the number with its code unchanged.
  return code ? `${fmtQty(v)} ${code}` : fmtQty(v);
}

/**
 * Pack-aware quantity label. Across the whole system a stock / order quantity is a
 * COUNT OF PACKS (SKU units), e.g. "Biovin 20Kg" qty 15 = 15 bags of 20 kg, and
 * "AGN 1L" qty 11 = 11 bottles of 1 L. `packSize` is how much one pack holds in `uom`.
 *
 * ALWAYS shown as "count × pack" so the number reads as a pack count, never as a
 * weight/volume — consistent regardless of pack size:
 *   15 × 20 KG · 9 × 10 KG · 11 × 1 LTR · 5 × 2 LTR
 * (falls back to the bare count when no UOM is known).
 */
export function fmtPackQty(qty: any, packSize?: any, uom?: string): string {
  const nq = fmtQty(qty);
  const u = (uom || '').trim();
  if (!u) return nq;
  const ps = Number(packSize) || 1;
  // The pack portion is a measure → render it UOM-friendly (e.g. 0.5 LTR → "500 ml").
  return `${nq} × ${fmtUom(ps, u)}`;
}

/** The pack count with a "pack(s)" word — use where a separate column already shows the pack size. */
export function fmtPacks(qty: any): string {
  const nq = fmtQty(qty);
  return `${nq} ${Number(qty) === 1 ? 'pack' : 'packs'}`;
}
